import { createRouter, createWebHistory } from 'vue-router';

import ParentLayout from '@/layouts/ParentLayout.vue';
import PublicLayout from '@/layouts/PublicLayout.vue';
import StudentLayout from '@/layouts/StudentLayout.vue';
import AuthLayout from '@/layouts/AuthLayout.vue';
import OnboardingLayout from '@/layouts/OnboardingLayout.vue';
import InfoLayout from '@/layouts/InfoLayout.vue';
import HomePage from '@/pages/home/HomePageV2.vue';
import UnderDevelopmentPage from '@/pages/home/UnderDevelopmentPage.vue';
import AboutPage from '@/pages/info/AboutPage.vue';
import FAQPage from '@/pages/info/FAQPage.vue';
import ContactPage from '@/pages/info/ContactPage.vue';
import TermsPage from '@/pages/info/TermsPage.vue';
import CookiesPage from '@/pages/info/CookiesPage.vue';
import PrivacyPage from '@/pages/info/PrivacyPage.vue';

import Login from '@/pages/auth/Login.vue';
import Register from '@/pages/auth/Register.vue';
import PaymentPage from '@/pages/auth/PaymentPage.vue';
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage.vue';
import WelcomePage from '@/pages/auth/WelcomePage.vue';
import OnboardingPage from '@/pages/onboarding/OnboardingPage.vue';

import ParentDashboard from '@/pages/parent/ParentDashboard.vue';
import SelectTrack from '@/pages/parent/SelectTrack.vue';

import CurriculumView from '@/pages/student/CurriculumView.vue';
import WorksheetsView from '@/pages/student/WorksheetsView.vue';
import LearnSection from '@/pages/student/LearnSection.vue';
import DiagnosticView from '@/pages/student/DiagnosticView.vue';
import ContinueLearningView from '@/pages/student/ContinueLearningView.vue';
import RevisionView from '@/pages/student/RevisionView.vue';
import ParentMetricsView from '@/pages/parent/ParentMetricsView.vue';
import SubscriptionPage from '@/pages/parent/SubscriptionPage.vue';
import ManageAccountPage from '@/pages/parent/ManageAccountPage.vue';
import LessonHubPage from '@/pages/learn/LessonHubPage.vue';
import TrackPage from '@/pages/track/TrackPage.vue';
import StudentHome from '@/pages/student/StudentHome.vue';

import { getSession, authReady } from '@/services/authService';

// Flip to `false` to bring the full site back live.
const SITE_UNDER_DEVELOPMENT = true;

const routes = [
  {
    path: '/',
    component: SITE_UNDER_DEVELOPMENT ? UnderDevelopmentPage : HomePage,
  },
  // ─── Public info pages ───────────────────────────────────────
  {
    path: '/info',
    component: InfoLayout,
    children: [
      { path: 'about',   component: AboutPage },
      { path: 'faq',     component: FAQPage },
      { path: 'contact', component: ContactPage },
      { path: 'terms',   component: TermsPage },
      { path: 'privacy', component: PrivacyPage },
      { path: 'cookies', component: CookiesPage },
    ],
  },

  {
    path: '/worksheets',
    component: PublicLayout,
    children: [
      { path: '', component: WorksheetsView },
    ],
  },

  {
    path: '/auth',
    component: AuthLayout,
    children: [
      { path: 'login', component: Login },
      { path: 'register', component: Register },
      { path: 'reset-password', component: ResetPasswordPage },
    ],
  },

  {
    path: '/payment',
    component: PaymentPage,
    meta: { requiresAuth: true },
  },

  {
    path: '/welcome',
    component: WelcomePage,
    meta: { requiresAuth: true },
  },

  {
    path: '/onboarding',
    component: OnboardingLayout,
    meta: { requiresAuth: true },
    children: [
      { path: '', component: OnboardingPage },
    ],
  },

  {
    path: '/parent',
    component: ParentLayout,
    meta: { requiresAuth: true },
    children: [
      { path: '', redirect: '/parent/dashboard' },
      { path: 'dashboard', component: ParentDashboard },
      { path: 'subscription', component: SubscriptionPage },
      { path: 'manage-account', component: ManageAccountPage },
      { path: 'select-track/:studentId', component: SelectTrack },
      { path: ':studentId/metrics', component: ParentMetricsView, meta: { role: 'parent' } },
      { path: ':studentId/track', component: TrackPage, meta: { role: 'parent' } },
    ],
  },

  {
    path: '/student',
    component: StudentLayout,
    meta: { requiresAuth: true },
    children: [
      { path: ':studentId/dashboard', component: StudentHome },
      { path: ':studentId/curriculum', component: CurriculumView },
      { path: ':studentId/worksheets', component: WorksheetsView },
      { path: 'learn/:sectionId', component: LearnSection },
      { path: ':studentId/continue', component: ContinueLearningView },
      { path: ':studentId/skills-check', component: DiagnosticView },
      // Old diagnostic route → redirect
      { path: ':studentId/diagnostic', redirect: to => `/student/${to.params.studentId}/skills-check` },
      { path: ':studentId/revision', component: RevisionView },
      { path: ':studentId/learn/:lessonId', component: LessonHubPage, meta: { role: 'child' } },
      // Old subtopic route → redirect to hub with topic query param
      {
        path: ':studentId/learn/:lessonId/:sectionGroupId',
        redirect: to => ({
          path: `/student/${to.params.studentId}/learn/${to.params.lessonId}`,
          query: { topic: to.params.sectionGroupId },
        }),
      },
      // Old practice routes → redirect to hub with practice query param
      {
        path: ':studentId/practice/:lessonId',
        redirect: to => ({
          path: `/student/${to.params.studentId}/learn/${to.params.lessonId}`,
          query: { practice: 'all' },
        }),
      },
      {
        path: ':studentId/practice/:lessonId/:sectionGroupId',
        redirect: to => ({
          path: `/student/${to.params.studentId}/learn/${to.params.lessonId}`,
          query: { topic: to.params.sectionGroupId },
        }),
      },
      { path: ':studentId/track', component: TrackPage, meta: { role: 'child' } },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition;
    if (to.hash) return { el: to.hash, behavior: 'smooth' };
    return { top: 0 };
  },
});

// ─── Navigation guard ────────────────────────────────────────
const AUTH_ROUTES = ['/auth/login', '/auth/register'];
const ONBOARDING_SAFE = ['/onboarding', '/payment', '/welcome'];

router.beforeEach(async (to, _from) => {
  // Site-wide gate: while under development, every route redirects to the banner page.
  if (SITE_UNDER_DEVELOPMENT && to.path !== '/') {
    return { path: '/' };
  }

  // Wait for Firebase to confirm auth state before every navigation.
  // Without this, a hard reload triggers the guard while _session is still null
  // (Firebase is async), causing an immediate redirect to /auth/login.
  await authReady;

  const session = getSession();
  const isAuthenticated = session !== null;
  const isOnboarded = session?.onboardingComplete === true;
  const requiresAuth = to.matched.some(r => r.meta.requiresAuth);

  // 1. Protected route but not logged in → login
  if (requiresAuth && !isAuthenticated) {
    return { path: '/auth/login' };
  }

  // 2. Logged-in user visiting auth pages → dashboard
  if (isAuthenticated && AUTH_ROUTES.includes(to.path)) {
    return { path: '/parent/dashboard' };
  }

  // 3. Logged-in but not onboarded → force onboarding
  //    (unless already heading there or to payment)
  if (isAuthenticated && !isOnboarded) {
    const safe = ONBOARDING_SAFE.some(p => to.path.startsWith(p));
    if (!safe && requiresAuth) {
      return { path: '/onboarding' };
    }
  }

  // 4. Already-onboarded user returning for upgrade onboarding — allow through.
  //    (No redirect needed; the guard only blocks non-onboarded users above.
  //     This comment documents the intentional exception for /onboarding?upgrade=true.)
});

export default router;
